import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaUser, FaBars, FaTimes } from 'react-icons/fa';
import api from '../../axios/api';
import { keys } from '../../config/keys';
import { toast } from 'react-toastify';
import { GlobalContext } from '../../contexts/GlobalContext';
import { BanknoteIcon, CircleUser, LogOut, Wallet, LogIn, UserPlus } from 'lucide-react';

const Navbar = () => {
  const [menuActive, setMenuActive] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const profileMenuRef = useRef(null);

  const {
    profilePicture,
    setProfilePicture,
    setRole,
    isLoggedIn,
    setLoggedIn,
    setUserId,
    setRedeemedPoints,
    collectedPoints,
    setCollectedPoints
  } = useContext(GlobalContext);

  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;

  const isActive = (path) => {
    if (path === '/') {
      return pathname === '/';
    }
    return pathname.toLowerCase().startsWith(path.toLowerCase());
  };

  const toggleMenu = () => {
    setMenuActive((prev) => !prev);
    setMobileProfileOpen(false);
  };

  const handleLinkClick = () => {
    setMenuActive(false);
    setProfileMenuOpen(false);
    setMobileProfileOpen(false);
  };

  const handleProfileMenuToggle = () => {
    setProfileMenuOpen((prev) => !prev);
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/signout');
      localStorage.removeItem('token');
      setLoggedIn(false);
    } catch (error) {
      console.error('Logout failed:', error.response ? error.response.data : error.message);
      toast.error('Logout failed. Please try again.');
    } finally {
      setProfileMenuOpen(false);
      setMobileProfileOpen(false);
      setMenuActive(false);
      navigate('/');
      toast.success('Logout successful!');
    }
  };

  const getProfileImgUrl = (pic) => {
    if (!pic || typeof pic !== 'string') return '';
    let cleanPic = pic.replace(/\\/g, '/').trim();
    if (
      cleanPic.startsWith('http://') ||
      cleanPic.startsWith('https://') ||
      cleanPic.startsWith('data:') ||
      cleanPic.startsWith('blob:')
    ) {
      return cleanPic;
    }
    cleanPic = cleanPic.replace(/^\//, '');
    return `${keys.ServicePath}/${cleanPic}`;
  };

  // Lock background scroll when mobile menu is open
  useEffect(() => {
    if (menuActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuActive]);

  // Close profile menu on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoggedIn(false);
        setRole('');
        setUserId('');
        setProfilePicture(false);
        return;
      }
      try {
        const response = await api.get('/api/user/userdetails', { skipLoader: true });
        if (response.data.isLoggedIn && response.data.user?.user) {
          const u = response.data.user.user;
          setLoggedIn(true);
          setProfilePicture(u.userProfilePic || u.profilePicture || u.profile_picture || '');
          setRole(u.role_code || '');
          setUserId(u.id || '');
          setRedeemedPoints(u.redeemed_points || 0);
          setCollectedPoints(u.collected_points || 0);
          return;
        }
      } catch (error) {
        try {
          const authResponse = await api.get('/api/auth/check', { skipLoader: true });
          if (authResponse.data.loggedIn) {
            setLoggedIn(true);
            setProfilePicture(authResponse.data.profilePicture || authResponse.data.userProfilePic || '');
            setRole(authResponse.data.role || '');
            setUserId(authResponse.data.id || '');
            setRedeemedPoints(authResponse.data.redeemed_points || 0);
            setCollectedPoints(authResponse.data.collected_points || 0);
            return;
          }
        } catch (authErr) {
          console.error('Error checking auth status:', authErr);
        }
        setLoggedIn(false);
        setRole('');
        setUserId('');
        setProfilePicture(false);
      }
    };

    checkAuthStatus();
  }, [setLoggedIn, setProfilePicture, setRole, setUserId, setRedeemedPoints, setCollectedPoints]);

  const navItems = [
    { label: 'Home', path: '/', active: isActive('/') },
    { label: 'Tree Metric', path: '/TreeRegistry', active: isActive('/TreeRegistry') },
    { label: 'Map', path: '/map', active: isActive('/map') },
    { label: 'Trees', path: '/trees', active: isActive('/trees') || isActive('/plantprofile') },
    { label: 'Upload', path: '/upload', active: isActive('/upload') },
    { label: 'Statistics', path: '/statistics/chart', active: pathname.includes('/statistics') },
    { label: 'Clients', path: '/clients', active: isActive('/clients') || isActive('/clientprofile') },
    { label: 'Projects', path: '/projects', active: isActive('/projects') || isActive('/project') },
    { label: 'Leaders', path: '/Leaders', active: isActive('/Leaders') || isActive('/userprofile') },
    { label: 'Contact Us', path: '/contact', active: isActive('/contact') },
  ];

  if (isLoggedIn) {
    navItems.push({ label: 'Dashboard', path: '/dashboard/stats', active: isActive('/dashboard') });
  }

  return (
    <header className="sticky top-0 z-[9999] bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" onClick={handleLinkClick} className="transition-transform duration-200 hover:opacity-90">
              <img src="/images/encoraa-logo-d.png" alt="Encoraa Logo" className="h-9 w-auto max-w-[200px] object-contain" />
            </Link>
          </div>

          {/* Mobile Hamburger Button */}
          <div className="flex xl:hidden">
            <button
              onClick={toggleMenu}
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none transition-colors duration-200 cursor-pointer"
            >
              {menuActive ? <FaTimes className="w-5 h-5" /> : <FaBars className="w-5 h-5" />}
            </button>
          </div>

          {/* Desktop Navigation Links */}
          <nav className="hidden xl:flex items-center space-x-1.5">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.path}
                onClick={handleLinkClick}
                className={`px-2 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide transition-colors duration-200 ${
                  item.active
                    ? 'bg-[#8BC34A] text-white shadow-xs'
                    : 'bg-[#ff2800] text-white hover:bg-[#d61e00]'
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* Profile Dropdown Menu */}
            <div
              className="relative ml-2 py-1"
              ref={profileMenuRef}
            >
              <button
                onClick={handleProfileMenuToggle}
                type="button"
                className="relative flex items-center justify-center w-8 h-8 rounded-full ring-2 ring-emerald-500/50 hover:ring-emerald-600 focus:outline-none transition-colors duration-200 cursor-pointer overflow-hidden bg-gray-100 p-0 m-0 border-0"
                title="Profile Menu"
              >
                {isLoggedIn && profilePicture ? (
                  <img
                    src={getProfileImgUrl(profilePicture)}
                    alt="Profile"
                    className="w-full h-full object-cover rounded-full"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.parentElement?.querySelector('.profile-fallback-icon');
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                ) : null}
                <FaUser
                  className="profile-fallback-icon text-gray-600 text-xs"
                  style={{ display: isLoggedIn && profilePicture ? 'none' : 'block' }}
                />
              </button>

              {/* Compact Profile Submenu Dropdown */}
              {profileMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-28 rounded-lg bg-white shadow-xl border border-gray-200 p-1.5 z-50 transition-all duration-200 flex flex-col gap-1.5">
                  {isLoggedIn ? (
                    <>
                      <Link
                        to="/dashboard/profile"
                        onClick={handleLinkClick}
                        className={`w-full h-8 box-border border-0 m-0 flex items-center justify-start gap-2 px-2.5 py-1 text-[11px] font-bold rounded-md leading-normal transition-colors duration-200 text-left ${
                          isActive('/profile')
                            ? '!bg-[#8BC34A] !text-white'
                            : '!bg-[#ff2800] !text-white hover:!bg-[#d61e00]'
                        }`}
                      >
                        <CircleUser className="w-3.5 h-3.5 text-white flex-shrink-0" />
                        <span className="truncate leading-normal">Profile</span>
                      </Link>

                      <Link
                        to="/dashboard/activity"
                        onClick={handleLinkClick}
                        className={`w-full h-8 box-border border-0 m-0 flex items-center justify-start gap-2 px-2.5 py-1 text-[11px] font-bold rounded-md leading-normal transition-colors duration-200 text-left ${
                          isActive('/activity')
                            ? '!bg-[#8BC34A] !text-white'
                            : '!bg-[#ff2800] !text-white hover:!bg-[#d61e00]'
                        }`}
                      >
                        <Wallet className="w-3.5 h-3.5 text-white flex-shrink-0" />
                        <span className="truncate leading-normal">{collectedPoints > 9000 ? `${Math.floor(collectedPoints / 1000)}K` : collectedPoints}</span>
                      </Link>

                      <Link
                        to="/dashboard/redeem"
                        onClick={handleLinkClick}
                        className={`w-full h-8 box-border border-0 m-0 flex items-center justify-start gap-2 px-2.5 py-1 text-[11px] font-bold rounded-md leading-normal transition-colors duration-200 text-left ${
                          isActive('/claim')
                            ? '!bg-[#8BC34A] !text-white'
                            : '!bg-[#ff2800] !text-white hover:!bg-[#d61e00]'
                        }`}
                      >
                        <BanknoteIcon className="w-3.5 h-3.5 text-white flex-shrink-0" />
                        <span className="truncate leading-normal">Redeem</span>
                      </Link>

                      <button
                        onClick={logout}
                        type="button"
                        className="w-full h-8 box-border border-0 m-0 flex items-center justify-start gap-2 px-2.5 py-1 text-[11px] font-bold rounded-md leading-normal !bg-[#ff2800] !text-white hover:!bg-[#d61e00] transition-colors duration-200 text-left cursor-pointer"
                      >
                        <LogOut className="w-3.5 h-3.5 text-white flex-shrink-0" />
                        <span className="truncate leading-normal">Logout</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        onClick={handleLinkClick}
                        className="w-full h-8 box-border border-0 m-0 flex items-center justify-start gap-2 px-2.5 py-1 text-[11px] font-bold rounded-md leading-normal !bg-[#ff2800] !text-white hover:!bg-[#d61e00] transition-colors duration-200 text-left"
                      >
                        <LogIn className="w-3.5 h-3.5 text-white flex-shrink-0" />
                        <span className="truncate leading-normal">Login</span>
                      </Link>
                      <Link
                        to="/signup"
                        onClick={handleLinkClick}
                        className="w-full h-8 box-border border-0 m-0 flex items-center justify-start gap-2 px-2.5 py-1 text-[11px] font-bold rounded-md leading-normal !bg-[#ff2800] !text-white hover:!bg-[#d61e00] transition-colors duration-200 text-left"
                      >
                        <UserPlus className="w-3.5 h-3.5 text-white flex-shrink-0" />
                        <span className="truncate leading-normal">Sign Up</span>
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile Drawer Navigation Menu - Centered Fixed-Width Badges */}
      {menuActive && (
        <div className="xl:hidden bg-white border-t border-gray-100 py-5 px-4 max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain transition-all duration-300 ease-in-out shadow-2xl">
          <div className="flex flex-col items-center justify-center space-y-2 mx-auto max-w-sm pb-8">
            
            {/* Nav Items - Same Fixed Width (w-64 sm:w-72), Centered, PC Color Scheme */}
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.path}
                onClick={handleLinkClick}
                className={`w-64 sm:w-72 py-2 px-3 rounded-md text-[11px] font-bold uppercase tracking-wider text-center transition-colors duration-200 ${
                  item.active
                    ? 'bg-[#8BC34A] text-white shadow-xs'
                    : 'bg-[#ff2800] text-white hover:bg-[#d61e00]'
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* Profile Section - Centered with same fixed width and click-to-expand */}
            <div className="w-64 sm:w-72 pt-3 mt-1 border-t border-gray-200 flex flex-col items-center gap-2">
              {isLoggedIn ? (
                <>
                  {/* Clickable Profile Card Header */}
                  <button
                    type="button"
                    onClick={() => setMobileProfileOpen((prev) => !prev)}
                    className="w-64 sm:w-72 flex items-center justify-between py-1.5 px-3 bg-gray-50 hover:bg-gray-100 rounded-md border border-gray-200/80 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center ring-1 ring-emerald-500/50">
                        {profilePicture ? (
                          <img
                            src={getProfileImgUrl(profilePicture)}
                            alt="Profile"
                            className="w-full h-full object-cover rounded-full"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.parentElement?.querySelector('.mobile-profile-fallback-icon');
                              if (fallback) fallback.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <FaUser
                          className="mobile-profile-fallback-icon text-gray-600 text-[10px]"
                          style={{ display: profilePicture ? 'none' : 'block' }}
                        />
                      </div>
                      <span className="text-[11px] font-bold text-gray-700">
                        {collectedPoints > 9000 ? `${Math.floor(collectedPoints / 1000)}K` : collectedPoints} Pts
                      </span>
                    </div>
                    <span className={`text-[10px] text-gray-500 font-bold transition-transform duration-200 ${mobileProfileOpen ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </button>

                  {/* Expandable Submenu Items */}
                  {mobileProfileOpen && (
                    <div className="w-64 sm:w-72 flex flex-col items-center gap-2 pt-1 transition-all duration-200">
                      <Link
                        to="/dashboard/profile"
                        onClick={handleLinkClick}
                        className={`w-64 sm:w-72 py-2 px-3 rounded-md text-[11px] font-bold uppercase tracking-wider text-center transition-colors duration-200 ${
                          isActive('/profile')
                            ? '!bg-[#8BC34A] !text-white'
                            : '!bg-[#ff2800] !text-white hover:!bg-[#d61e00]'
                        }`}
                      >
                        Profile
                      </Link>

                      <Link
                        to="/dashboard/activity"
                        onClick={handleLinkClick}
                        className={`w-64 sm:w-72 py-2 px-3 rounded-md text-[11px] font-bold uppercase tracking-wider text-center transition-colors duration-200 ${
                          isActive('/activity')
                            ? '!bg-[#8BC34A] !text-white'
                            : '!bg-[#ff2800] !text-white hover:!bg-[#d61e00]'
                        }`}
                      >
                        Wallet
                      </Link>

                      <Link
                        to="/dashboard/redeem"
                        onClick={handleLinkClick}
                        className={`w-64 sm:w-72 py-2 px-3 rounded-md text-[11px] font-bold uppercase tracking-wider text-center transition-colors duration-200 ${
                          isActive('/claim')
                            ? '!bg-[#8BC34A] !text-white'
                            : '!bg-[#ff2800] !text-white hover:!bg-[#d61e00]'
                        }`}
                      >
                        Redeem
                      </Link>

                      <button
                        onClick={logout}
                        type="button"
                        className="w-64 sm:w-72 py-2 px-3 rounded-md text-[11px] font-bold uppercase tracking-wider text-center !bg-[#ff2800] !text-white hover:!bg-[#d61e00] transition-colors cursor-pointer border-0"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex gap-2 w-64 sm:w-72">
                  <Link
                    to="/login"
                    onClick={handleLinkClick}
                    className="flex-1 py-2 px-2 rounded-md text-[11px] font-bold uppercase tracking-wider text-center !bg-[#ff2800] !text-white hover:!bg-[#d61e00] transition-colors"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    onClick={handleLinkClick}
                    className="flex-1 py-2 px-2 rounded-md text-[11px] font-bold uppercase tracking-wider text-center !bg-[#ff2800] !text-white hover:!bg-[#d61e00] transition-colors"
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
